import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { BASE_API_URL, fetchApi } from '../utils/fetchApi';
import { hashPassword } from '../utils/helpers';

type TemperatureUnit = 'Celsius' | 'Fahrenheit';
interface UserContextType {
  isLoggedIn: boolean;
  login: (username: string, password: string, selectedRole?: string) => Promise<void>;
  logout: () => void;
  unit: TemperatureUnit;
  toggleUnit: () => void;
  updatePassword: (username: string, newPassword: string) => Promise<void>;
  availableRoles: string[] | null;
  setAvailableRoles: React.Dispatch<React.SetStateAction<string[] | null>>;
  forgetPassword:  (email: string) => Promise<void>;
  hasUnreadMessages: boolean;
  setHasUnreadMessages: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const fireClientInfo = (token: string | null): void => {
  if (!token) return;
  fetch(BASE_API_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'update_client_info',
      payload: {
        jwt_token: token,
        domain: window.location.hostname,
        user_agent: navigator.userAgent,
      },
    }),
  }).catch(() => {});
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!localStorage.getItem('authToken'));
  const [unit, setUnit] = useState<TemperatureUnit>('Celsius');
  const [availableRoles, setAvailableRoles] = useState<string[] | null>(null);
  const [hasUnreadMessages, setHasUnreadMessages] = useState<boolean>(false);


  const login = async (username: string, password: string, selectedRole?: string): Promise<void> => {
    let token: string;
    let expiration: string;
    let refreshToken: string;
    let refreshTokenExpiration: string;
    let roles: string[] = [];
    const pwhash = await hashPassword(password);
    const payload: Record<string, any> = {
      username_or_email: username,
      pwhash,
    };
    if (selectedRole) {
      payload.login_level = selectedRole;
    }
    const response = await fetch(BASE_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'get_jw_token',
        payload,
      }),
    });
    const result = await response.json();

    if (result.error) {
      console.log("Login error", result.error);
      throw new Error(result.error);
    }

    if (Array.isArray(result)) {
      [token, roles, expiration, refreshToken, refreshTokenExpiration] = result;
    } else {
      ({ 
        token, 
        rights: roles, 
        expiration, 
        refresh_token: refreshToken, 
        refresh_token_expiration: refreshTokenExpiration 
      } = result as { 
        token: string; 
        expiration: string; 
        rights: string[]; 
        refresh_token: string; 
        refresh_token_expiration: string; 
      });
    }

    if (token === null) {
      throw new Error('invalidCredentials');
    }
    if (roles && roles.length > 1 && !selectedRole) {
      setAvailableRoles(roles);
      return;
    }
    const cleanToken = token.replace(/^"|"$/g, '');
    const cleanRefreshToken = refreshToken ? refreshToken.replace(/^"|"$/g, '') : '';

    fireClientInfo(cleanToken);

    localStorage.setItem('username', username);
    localStorage.setItem('authToken', cleanToken);
    localStorage.setItem('tokenExpiration', expiration);
    localStorage.setItem('refreshToken', cleanRefreshToken);
    localStorage.setItem('refreshTokenExpiration', refreshTokenExpiration);
    localStorage.setItem('selectedRole', selectedRole ?? roles[0]);
    setIsLoggedIn(true);
    setAvailableRoles(null); // Reset after login
  };

  const updatePassword = async (_username: string, newPassword: string): Promise<void> => {
    const pwhash = await hashPassword(newPassword);
    await fetchApi('change_password', { new_pwhash: pwhash });
  };

  const forgetPassword = async (email: string) => {
    const response = await fetch(BASE_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'initiate_password_reset',
        payload: {
          email,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to reset your password');
    }
  }

  const logout = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiration');
    localStorage.removeItem('username');
    localStorage.removeItem('selectedRole');
    localStorage.removeItem('buildingId');
    localStorage.removeItem('apartmentId');
    localStorage.removeItem('apartmentName');
    setIsLoggedIn(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
    fireClientInfo(token);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setHasUnreadMessages(false);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const apartmentId = localStorage.getItem('apartmentId');
    const buildingId = localStorage.getItem('buildingId');
    const msgPayload: Record<string, any> = { jwt_token: token };
    if (apartmentId) {
      msgPayload.apartment_ids = [parseInt(apartmentId)];
      msgPayload.building_ids = [parseInt(buildingId ?? '0')];
    }
    fetch(BASE_API_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation: 'get_messages', payload: msgPayload }),
    })
      .then((res) => res.json())
      .then((data: { is_read: boolean }[]) => {
        if (Array.isArray(data)) {
          setHasUnreadMessages(data.some((m) => !m.is_read));
        }
      })
      .catch(() => {});
  }, [isLoggedIn]);

  const toggleUnit = () => setUnit((prevUnit) => (prevUnit === 'Celsius' ? 'Fahrenheit' : 'Celsius'));

  return (
    <UserContext.Provider value={{ isLoggedIn, login, logout, unit, toggleUnit, updatePassword, availableRoles, setAvailableRoles, forgetPassword, hasUnreadMessages, setHasUnreadMessages }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
